import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import createAxios from "@/lib/axios";
import { Spinner } from "@/components/Spinner";
import { Subject } from "@/types";
import { Card } from "@/components/ui/card";

export const Route = createFileRoute("/dashboard/quiz/new/select-subject")({
  component: SubjectForm,
  loader: async ({ context }) => {
    const user = await context.auth.getUser();
    const client = await createAxios();
    const res = await client.get<{ subjects: Subject[] }>(
      `${import.meta.env.VITE_BACKEND_URL}/subjects`,
    );
    const subjects = res.data.subjects;
    if (!subjects) {
      throw new Error("No subjects found");
    }
    return { subjects, userId: user!.uid };
  },
});

function SubjectForm() {
  const { subjects } = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const FormSchema = z.object({
    subject: z.coerce.number().int({ message: "Please select a subject" }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      subject: subjects[0]?.id ?? 0,
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    navigate({
      to: "/dashboard/quiz/new",
      search: {
        subject_id: data.subject,
      },
    });
  }

  return (
    <div className="light relative flex w-full bg-primary-foreground text-primary">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => onSubmit(data))}
          className="ml-8 flex w-full flex-grow flex-col justify-center gap-6"
        >
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2">
                <FormLabel className="text-5xl font-bold">
                  Select Subject
                </FormLabel>
                <RadioGroup
                  onValueChange={(val) => field.onChange(Number(val))}
                  defaultValue={field.value.toString()}
                  className="flex flex-col gap-2"
                >
                  {subjects.map((subject) => (
                    <SubjectItem key={subject.name} subject={subject} />
                  ))}
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />
          <SubmitButton isLoading={form.formState.isSubmitting} />
        </form>
      </Form>
    </div>
  );
}

function SubmitButton({ isLoading }: { isLoading: boolean }) {
  return (
    <Button
      type="submit"
      className="w-48"
      disabled={isLoading}
      aria-label="select topics"
    >
      {isLoading && <Spinner />}
      Select Topics
    </Button>
  );
}

function SubjectItem({ subject }: { subject: Subject }) {
  const iconPath = `/assets/icons/${subject.id}.svg`;
  const radioId = `subject-radio-${subject.id}`;

  return (
    <Card className="flex w-2/5 items-center gap-4 p-4">
      <FormControl>
        <RadioGroupItem
          value={subject.id.toString()}
          id={radioId}
          className="mr-2"
        />
      </FormControl>

      <FormLabel
        htmlFor={radioId}
        className="flex h-full w-full cursor-pointer items-center gap-4"
      >
        <img src={iconPath} alt={subject.displayName} className="size-24" />
        <p>{subject.displayName}</p>
      </FormLabel>
    </Card>
  );
}
